import { useState, useEffect } from "react";
import axios from "axios";
import {
  Mail, Lock, User, Building2, Eye, EyeOff,
  ChevronLeft, ChevronRight, Check, X, Plus, Trash2, Briefcase, Users
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { API } from "../App";
import { useI18n } from "../i18n/I18nProvider";

const SUPPORTED_FRAMEWORKS = [
  { id: "iso27001", label: "ISO/IEC 27001" },
  { id: "nist_scf", label: "NIST SCF" },
  { id: "cis_v8", label: "CIS v8" },
  { id: "iso20000", label: "ISO/IEC 20000" },
];

const passwordChecks = (pw) => ({
  length: pw.length >= 10 && pw.length <= 128,
  classes: [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length >= 3,
});

const StepDots = ({ total, current }) => (
  <div className="flex items-center justify-center gap-2 mb-6" data-testid="wizard-progress">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`h-1.5 rounded-full transition-all ${
          i < current ? "w-6 bg-primary"
          : i === current ? "w-10 bg-primary"
          : "w-6 bg-muted"
        }`}
      />
    ))}
  </div>
);

const FrameworkSelector = ({ value, onChange, testidPrefix = "fw" }) => (
  <div className="grid grid-cols-2 gap-2">
    {SUPPORTED_FRAMEWORKS.map((fw) => {
      const checked = value.includes(fw.id);
      return (
        <button
          key={fw.id}
          type="button"
          data-testid={`${testidPrefix}-${fw.id}`}
          onClick={() =>
            onChange(checked ? value.filter((x) => x !== fw.id) : [...value, fw.id])
          }
          className={`p-3 rounded-md border text-left text-sm transition-colors ${
            checked
              ? "border-primary bg-primary/10"
              : "border-border hover:border-primary/40"
          }`}
        >
          <div className="flex items-center justify-between">
            <span>{fw.label}</span>
            {checked && <Check className="w-4 h-4 text-primary" />}
          </div>
        </button>
      );
    })}
  </div>
);

const RegisterWizard = ({ onSubmit, loading }) => {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organization_name: "",
    business_type: "internal",
    industry: "",
    country: "",
    active_frameworks: ["iso27001"],
    expected_users: 5,
    consultant_tenants: [],
  });

  const totalSteps = form.business_type === "consultant" ? 4 : 3;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const pwc = passwordChecks(form.password);

  // i18n helper for count-dependent phrases (register.price.<base>_one/_other)
  const pricePhrase = (count, base) =>
    t(`register.price.${base}_${count === 1 ? "one" : "other"}`).replace("{n}", count);

  // Sprint 4D: live price preview on step 3
  const [pricePreview, setPricePreview] = useState(null);
  useEffect(() => {
    if (step !== 2) return;
    if (!form.active_frameworks.length || !form.expected_users) return;
    const ctrl = new AbortController();
    axios
      .post(
        `${API}/subscription/preview-price`,
        {
          tier_id: "professional",
          standards: form.active_frameworks.length,
          users: Number(form.expected_users) || 0,
        },
        { signal: ctrl.signal }
      )
      .then((res) => setPricePreview(res.data))
      .catch(() => {});
    return () => ctrl.abort();
  }, [step, form.active_frameworks.length, form.expected_users]);

  const validateStep = () => {
    if (step === 0) {
      if (!form.name.trim()) return t("register.err_name_required");
      if (!form.email.trim()) return t("register.err_email_required");
      if (!pwc.length || !pwc.classes)
        return t("register.err_password_requirements");
    }
    if (step === 1) {
      if (!form.organization_name.trim()) return t("register.err_org_required");
    }
    if (step === 2) {
      if (form.active_frameworks.length === 0)
        return t("register.err_standard_required");
      if (!form.expected_users || form.expected_users < 1)
        return t("register.err_users_min");
    }
    if (step === 3 && form.business_type === "consultant") {
      if (form.consultant_tenants.length === 0)
        return t("register.err_tenant_required");
      for (const tenant of form.consultant_tenants) {
        if (!tenant.name?.trim()) return t("register.err_tenant_name_required");
        if (!tenant.active_frameworks?.length)
          return t("register.err_tenant_standard_required").replace("{name}", tenant.name);
      }
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  };

  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      organization_name: form.organization_name.trim(),
      business_type: form.business_type,
      industry: form.industry || undefined,
      country: form.country || undefined,
      active_frameworks: form.active_frameworks,
      expected_users: Number(form.expected_users) || undefined,
    };
    if (form.business_type === "consultant") {
      payload.consultant_tenants = form.consultant_tenants.map((tenant) => ({
        name: tenant.name.trim(),
        active_frameworks: tenant.active_frameworks,
        expected_users: Number(tenant.expected_users) || undefined,
      }));
    }
    onSubmit(payload);
  };

  const addTenant = () =>
    update({
      consultant_tenants: [
        ...form.consultant_tenants,
        { name: "", active_frameworks: ["iso27001"], expected_users: 5 },
      ],
    });

  const updateTenant = (idx, patch) => {
    const list = [...form.consultant_tenants];
    list[idx] = { ...list[idx], ...patch };
    update({ consultant_tenants: list });
  };

  const removeTenant = (idx) =>
    update({
      consultant_tenants: form.consultant_tenants.filter((_, i) => i !== idx),
    });

  return (
    <div className="space-y-4" data-testid="register-wizard">
      <StepDots total={totalSteps} current={step} />

      {step === 0 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reg-name">{t("auth.name")}</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="reg-name" className="pl-10" placeholder={t("register.full_name_placeholder")}
                value={form.name} onChange={(e) => update({ name: e.target.value })}
                autoComplete="name" data-testid="register-name-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-email">{t("auth.email")}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="reg-email" type="email" className="pl-10" placeholder={t("register.email_placeholder")}
                value={form.email} onChange={(e) => update({ email: e.target.value })}
                autoComplete="email" data-testid="register-email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-pw">{t("auth.password")}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="reg-pw" type={showPassword ? "text" : "password"}
                className="pl-10 pr-10" placeholder={t("register.password_placeholder")}
                value={form.password} onChange={(e) => update({ password: e.target.value })}
                autoComplete="new-password" minLength={10} maxLength={128}
                data-testid="register-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t("register.hide_password") : t("register.show_password")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1 mt-1">
              <li className={`flex items-center gap-1 ${pwc.length ? "text-emerald-500" : ""}`}>
                {pwc.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {t("register.pwd_check_length")}
              </li>
              <li className={`flex items-center gap-1 ${pwc.classes ? "text-emerald-500" : ""}`}>
                {pwc.classes ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {t("register.pwd_check_classes")}
              </li>
            </ul>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reg-org">{t("auth.org_name")}</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="reg-org" className="pl-10" placeholder={t("register.org_name_placeholder")}
                value={form.organization_name}
                onChange={(e) => update({ organization_name: e.target.value })}
                autoComplete="organization" data-testid="register-org-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("register.business_type")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="business-type-internal"
                onClick={() => update({ business_type: "internal", consultant_tenants: [] })}
                className={`p-3 rounded-md border text-left transition-colors ${
                  form.business_type === "internal"
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/40"
                }`}>
                <Building2 className="w-5 h-5 mb-1 text-primary" />
                <div className="font-medium text-sm">{t("register.internal_team")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("register.internal_team_desc")}
                </div>
              </button>
              <button type="button" data-testid="business-type-consultant"
                onClick={() => update({ business_type: "consultant" })}
                className={`p-3 rounded-md border text-left transition-colors ${
                  form.business_type === "consultant"
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/40"
                }`}>
                <Briefcase className="w-5 h-5 mb-1 text-primary" />
                <div className="font-medium text-sm">{t("register.consultant_mssp")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("register.consultant_mssp_desc")}
                </div>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="reg-industry">{t("register.industry")}</Label>
              <Input id="reg-industry" value={form.industry}
                onChange={(e) => update({ industry: e.target.value })}
                placeholder={t("register.industry_placeholder")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-country">{t("register.country")}</Label>
              <Input id="reg-country" value={form.country}
                onChange={(e) => update({ country: e.target.value })}
                placeholder={t("register.country_placeholder")} />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("register.standards_label")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("register.standards_hint")}
            </p>
            <FrameworkSelector
              value={form.active_frameworks}
              onChange={(v) => update({ active_frameworks: v })}
              testidPrefix="primary-fw"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-users">{t("register.expected_users")}</Label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="reg-users" type="number" min={1} max={10000}
                className="pl-10" value={form.expected_users}
                onChange={(e) => update({ expected_users: Number(e.target.value) })}
                data-testid="register-expected-users" />
            </div>
            <p className="text-xs text-muted-foreground">
              {t("register.expected_users_hint")}
            </p>
          </div>
          {pricePreview && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-1"
              data-testid="price-preview">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {t("register.price.estimated_plan")}
                </span>
                <span className="text-lg font-heading font-bold text-primary">
                  ${pricePreview.total_monthly}
                  <span className="text-xs font-normal text-muted-foreground">/mo</span>
                </span>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>
                  {t("register.price.base")} ${pricePreview.base} + {pricePhrase(pricePreview.extra_standards, "extra_standard")} (${pricePreview.standards_cost})
                  {" + "}{pricePhrase(pricePreview.extra_users, "extra_user")} (${pricePreview.users_cost})
                </div>
                <div className="opacity-75">
                  {t("register.price.includes")} {pricePhrase(pricePreview.included_standards, "standard")} {t("register.price.and")}{" "}
                  {t("register.price.users").replace("{n}", pricePreview.included_users)}.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 3 && form.business_type === "consultant" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>{t("register.client_tenants")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("register.client_tenants_hint")}
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addTenant}
              data-testid="add-tenant-btn">
              <Plus className="w-3 h-3 mr-1" /> {t("register.add_tenant")}
            </Button>
          </div>
          {form.consultant_tenants.length === 0 && (
            <div className="p-4 border border-dashed border-border rounded-md text-center text-sm text-muted-foreground">
              {t("register.no_tenants_yet")}
            </div>
          )}
          {form.consultant_tenants.map((tenant, idx) => (
            <div key={idx} className="border border-border rounded-md p-3 space-y-3"
              data-testid={`tenant-${idx}`}>
              <div className="flex items-center justify-between">
                <Badge variant="secondary">{t("register.tenant_number").replace("{n}", idx + 1)}</Badge>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => removeTenant(idx)} data-testid={`remove-tenant-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("register.tenant_name")}</Label>
                  <Input value={tenant.name}
                    onChange={(e) => updateTenant(idx, { name: e.target.value })}
                    placeholder={t("register.tenant_name_placeholder")}
                    data-testid={`tenant-${idx}-name`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("register.expected_users")}</Label>
                  <Input type="number" min={1} max={10000} value={tenant.expected_users}
                    onChange={(e) => updateTenant(idx, { expected_users: Number(e.target.value) })}
                    data-testid={`tenant-${idx}-users`} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("register.standards")}</Label>
                <FrameworkSelector
                  value={tenant.active_frameworks || []}
                  onChange={(v) => updateTenant(idx, { active_frameworks: v })}
                  testidPrefix={`tenant-${idx}-fw`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <Button type="button" variant="ghost" onClick={prev}
          disabled={step === 0 || loading} data-testid="wizard-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> {t("back")}
        </Button>
        {step < totalSteps - 1 ? (
          <Button type="button" onClick={next} disabled={loading} data-testid="wizard-next">
            {t("next")} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button type="button" onClick={submit} disabled={loading} data-testid="wizard-submit">
            {loading ? t("register.creating_account") : t("register.create_account")}
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        {t("register.legal_disclaimer")}
      </p>
    </div>
  );
};

export default RegisterWizard;
